import React, { useState, useEffect } from 'react';
import { useBiometric } from '@/context/BiometricContext';
import { Button } from '@vaiu/ui/button';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import {
  Fingerprint,
  Shield,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { i18n } from '@/utils/i18n';

interface BiometricLoginProps {
  onSuccess: (token: string) => void;
  onError: (error: string) => void;
  disabled?: boolean;
  className?: string;
  username?: string;  // Add username prop
}

const BiometricLogin: React.FC<BiometricLoginProps> = ({
  onSuccess,
  onError,
  disabled = false,
  className = '',
  username
}) => {
  const {
    support,
    isLoading,
    settings,
    authenticate,
    authenticateConditional,
    refreshSettings,
    biometricError,
    setBiometricError
  } = useBiometric();

  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);
  const [conditionalAuthStarted, setConditionalAuthStarted] = useState(false);
  const [authController, setAuthController] = useState<AbortController | null>(null);
  const [settingsHydrated, setSettingsHydrated] = useState(false);

  useEffect(() => {
    if (!support?.isSupported || !support.isAvailable) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await refreshSettings();
      } finally {
        if (!cancelled) {
          setSettingsHydrated(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [support?.isSupported, support?.isAvailable, refreshSettings]);

  // Clear errors when component unmounts or props change
  useEffect(() => {
    return () => {
      setBiometricError(null);
      // Cancel any ongoing authentication requests
      if (authController) {
        authController.abort();
        setAuthController(null);
      }
    };
  }, [setBiometricError, authController]);

  // Start conditional authentication on mount if supported
  useEffect(() => {
    const startConditionalAuth = async () => {
      if (
        !support?.isSupported ||
        !support.isAvailable ||
        !settingsHydrated ||
        !settings?.enabled ||
        conditionalAuthStarted ||
        isAuthenticating
      ) {
        return;
      }

      try {
        setConditionalAuthStarted(true);
        
        // Create AbortController for this authentication request
        const controller = new AbortController();
        setAuthController(controller);
        
        const result = await authenticateConditional(controller.signal, username);
        
        if (result.success && result.token && !controller.signal.aborted) {
          onSuccess(result.token);
        }
      } catch (error) {
        // Conditional authentication failure is silent - user can still use manual authentication
        console.log('Conditional authentication not available or failed silently');
      } finally {
        setAuthController(null);
      }
    };

    if (support?.isSupported && support.isAvailable && settingsHydrated && settings?.enabled) {
      startConditionalAuth();
    }
    
    // Cleanup function to abort any ongoing authentication
    return () => {
      if (authController) {
        authController.abort();
        setAuthController(null);
      }
    };
  }, [
    support,
    settings,
    settingsHydrated,
    authenticateConditional,
    onSuccess,
    conditionalAuthStarted,
    isAuthenticating,
    authController,
    username
  ]);

  const handleBiometricLogin = async () => {
    if (!support?.isSupported || !support.isAvailable) {
      onError('Biometric authentication is not available on this device');
      return;
    }

    // Prevent multiple simultaneous authentication requests
    if (isAuthenticating) {
      console.log('Authentication already in progress, ignoring request');
      return;
    }

    // Cancel any ongoing conditional authentication
    if (authController) {
      authController.abort();
      setAuthController(null);
    }

    setIsAuthenticating(true);
    setHasAttempted(true);
    setBiometricError(null);

    try {
      // Create new AbortController for this authentication request
      const controller = new AbortController();
      setAuthController(controller);
      
      const result = await authenticate(undefined, controller.signal, username);
      
      if (result.success && result.token && !controller.signal.aborted) {
        onSuccess(result.token);
      } else if (!controller.signal.aborted) {
        onError(result.error || 'Biometric authentication failed');
      }
    } catch (error) {
      if (!authController?.signal.aborted) {
        const errorMessage = error instanceof Error ? error.message : 'Biometric authentication failed';
        onError(errorMessage);
      }
    } finally {
      setIsAuthenticating(false);
      setAuthController(null);
    }
  };

  // Don't show if biometric is not supported
  if (isLoading) {
    return (
      <div className={`space-y-2 ${className}`}>
        <Button
          variant="outline"
          className="w-full"
          disabled={true}
        >
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Checking biometric support...
        </Button>
      </div>
    );
  }

  if (!support?.isSupported) {
    return null; // Don't show anything if not supported
  }

  if (!support.isAvailable) {
    return (
      <div className={`space-y-2 ${className}`}>
        <Button
          variant="outline"
          className="w-full"
          disabled={true}
        >
          <Shield className="h-4 w-4 mr-2" />
          Biometric authentication unavailable
        </Button>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            Set up fingerprint or face recognition on your device to use biometric login.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!settingsHydrated) {
    return (
      <div className={`space-y-2 ${className}`}>
        <Button variant="outline" className="w-full" disabled={true}>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Loading biometric options...
        </Button>
      </div>
    );
  }

  if (!settings?.enabled) {
    return null; // Don't show if user hasn't enabled biometric authentication
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <Button
        onClick={handleBiometricLogin}
        variant="outline"
        className="w-full"
        disabled={disabled || isAuthenticating}
      >
        {isAuthenticating ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Authenticating...
          </>
        ) : (
          <>
            <Fingerprint className="h-4 w-4 mr-2" />
            {i18n.__('login.with_biometric') || 'Sign in with biometric'}
          </>
        )}
      </Button>

      {/* Show error if authentication failed */}
      {biometricError && hasAttempted && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            {biometricError}
          </AlertDescription>
        </Alert>
      )}

      {/* Show success message for conditional authentication */}
      {conditionalAuthStarted && !biometricError && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            Biometric authentication is ready. Use your fingerprint or face recognition to sign in.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default BiometricLogin; 